import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import { NoteBackendModel, NoteModel } from '../models/note.model';
import {
  InitialModel,
  LecturerCreateModel,
  LecturerUpdateModel,
  UserBackendListModel,
  UserBackendModel,
  UserModel,
  UserUpdateModel,
} from '../models/user.model';
import { getUserInitials } from '../utils/user.utils';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient) {}
  public currentUser$ = new BehaviorSubject<UserModel | null>(null);
  public userIsAdmin$ = new BehaviorSubject<boolean>(
    this.currentUser$.value?.roleId === 1
  );
  private user: UserModel;
  private userInitials: InitialModel;

  public setUser(user: UserModel): void {
    this.user = user;
    this.userInitials = getUserInitials(user);
  }

  public getUser(): UserModel {
    return this.user;
  }

  public getCurrentUser(): Observable<UserModel> {
    return this.currentUser$.asObservable();
  }

  public getUserInitials(): InitialModel {
    this.userInitials.text =
      `${this.currentUser$.value.firstname.charAt(0)}${this.currentUser$.value.lastname.charAt(0)}`?.toUpperCase();
    return this.userInitials;
  }

  public userIsAdmin(user: UserModel): boolean {
    return user?.roleId === 1;
  }

  public getUserById(userId: number): Observable<UserModel> {
    const formData = new FormData();
    formData.append('mode', 'getUser');
    formData.append('id_user', userId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendModel;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => this.parseBackendUser(response.data)),
        catchError(() => {
          return throwError(() => new Error('User fetch failed'));
        })
      );
  }

  public getLecturerDetails(lecturerId: number): Observable<UserModel> {
    const formData = new FormData();
    formData.append('mode', 'getLecturerDetails');
    formData.append('id_user', lecturerId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendModel;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => this.parseBackendUser(response.data)),
        catchError(() => {
          return throwError(() => new Error('User fetch failed'));
        })
      );
  }

  public getAllUsers(): Observable<UserModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllUsers');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendListModel[];
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response =>
          response.data?.map(user => this.parseBackendListModel(user))
        ),
        catchError(() => {
          return throwError(() => new Error('User fetch failed'));
        })
      );
  }

  public updateUser(user: UserUpdateModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateUser');
    formData.append('id', user.id.toString());
    formData.append('email', user.email?.trim());
    user.salutation && formData.append('salutation', user.salutation);
    formData.append('first_name', user.firstName?.trim());
    formData.append('last_name', user.lastName?.trim());
    user.birthdate &&
      formData.append(
        'birth_date',
        moment(user.birthdate).format('YYYY-MM-DD')
      );
    user.jobTitle && formData.append('job_title', user.jobTitle);
    user.phoneNumber && formData.append('phone_number', user.phoneNumber);
    user.street && formData.append('street', user.street);
    user.houseNumber && formData.append('street_nr', user.houseNumber);
    user.zipCode && formData.append('postal_code', user.zipCode);
    user.city && formData.append('city', user.city);
    user.country && formData.append('country', user.country);
    formData.append('bed_member', user.bedMember ? '1' : '0');
    user.bedMemberNumber &&
      formData.append('bed_member_nr', user.bedMemberNumber);
    user.avatar && formData.append('avatar', user.avatar);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('User update failed'));
        })
      );
  }

  public getActiveLecturers(): Observable<UserModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getActiveLecturers');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendListModel[];
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response =>
          response.data?.map(user => this.parseBackendListModel(user))
        ),
        catchError(() => {
          return throwError(() => new Error('Active lecturers fetch failed'));
        })
      );
  }

  public getAllLecturers(): Observable<UserModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllLecturers');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendListModel[];
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response =>
          response.data?.map(user => this.parseBackendListModel(user))
        ),
        catchError(() => {
          return throwError(() => new Error('Lecturer fetch failed'));
        })
      );
  }

  public createLecturer(lecturer: LecturerCreateModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'createLecturer');
    formData.append('first_name', lecturer.firstname);
    formData.append('last_name', lecturer.lastname);
    formData.append('email', lecturer.email);
    formData.append('phone_number', lecturer.phone_number);
    formData.append('job_title', lecturer.job_title);
    formData.append('description1', lecturer.description1);
    formData.append('description2', lecturer.description2);
    formData.append('description3', lecturer.description3);
    formData.append('field_of_activity', lecturer.field_of_activity);
    formData.append('trainings', JSON.stringify(lecturer.trainings));
    formData.append('career_steps', JSON.stringify(lecturer.career_steps));
    formData.append('publications', JSON.stringify(lecturer.publications));
    formData.append('engagements', JSON.stringify(lecturer.engagements));
    formData.append('contributions', JSON.stringify(lecturer.contributions));
    lecturer.avatar && formData.append('avatar', lecturer.avatar);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Lecturer creation failed'));
        })
      );
  }

  public updateLecturer(lecturer: LecturerUpdateModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateLecturer');
    formData.append('id', lecturer.id.toString());
    formData.append('first_name', lecturer.firstname?.trim());
    formData.append('last_name', lecturer.lastname?.trim());
    formData.append('email', lecturer.email?.trim());
    lecturer.phone_number &&
      formData.append('phone_number', lecturer.phone_number?.trim());
    lecturer.job_title &&
      formData.append('job_title', lecturer.job_title?.trim());
    lecturer.description1 &&
      formData.append('description1', lecturer.description1?.trim());
    lecturer.description2 &&
      formData.append('description2', lecturer.description2?.trim());
    lecturer.description3 &&
      formData.append('description3', lecturer.description3?.trim());
    lecturer.field_of_activity &&
      formData.append('field_of_activity', lecturer.field_of_activity?.trim());
    formData.append('trainings', JSON.stringify(lecturer.trainings));
    formData.append('career_steps', JSON.stringify(lecturer.career_steps));
    formData.append('publications', JSON.stringify(lecturer.publications));
    formData.append('engagements', JSON.stringify(lecturer.engagements));
    formData.append('contributions', JSON.stringify(lecturer.contributions));
    lecturer.avatar && formData.append('avatar', lecturer.avatar);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Lecturer update failed'));
        })
      );
  }

  notes: NoteModel[] = [
    {
      id: 1,
      text: 'Testnotiz',
      createdAt: new Date('2024-07-01T12:00:00'),
      updatedAt: new Date('2024-08-01T13:57:00'),
      user: {
        id: 1,
        firstname: 'Vanessa',
        lastname: 'Diel',
        email: '',
      },
    },
    {
      id: 2,
      text: 'Das ist eine neue Notiz mit einem längeren Text, damit man ggf. besser erkennen kann wie sich das Note-Element verhält, wenn der Text länger ist.',
      createdAt: new Date('2024-10-31T08:57:00'),
      user: {
        id: 2,
        firstname: 'Stefan',
        lastname: 'Bohmann',
        email: '',
      },
    },
  ];

  public getUserNotes(userId: number): Observable<NoteModel[]> {
    // const formData = new FormData();
    // formData.append('mode', 'getNotes');
    // formData.append('id_user', userId.toString());

    // return this.http
    //   .post<{
    //     success: boolean;
    //     message: string;
    //     data: NoteBackendModel[];
    //   }>(environment.authority_short + 'user.php', formData)
    //   .pipe(
    //     map(response =>
    //       response.data?.map(note => this.parseBackendNote(note))
    //     ),
    //     catchError(() => {
    //       return throwError(() => new Error('User notes fetch failed'));
    //     })
    //   );
    return new Observable<NoteModel[]>(observer => {
      observer.next(this.notes);
      observer.complete();
    });
  }

  public parseBackendUser(user: UserBackendModel): UserModel {
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      salutation: user.salutation,
      cooperationName: user.cooperation_name,
      firstname: user.first_name,
      lastname: user.last_name,
      email: user.email,
      avatarPath: user.avatar_path,
      address: {
        street: user.street,
        houseNumber: user.street_nr,
        zipCode: user.postal_code,
        city: user.city,
        country: user.country,
      },
      phoneNumber: user.phone_number,
      birthdate:
        user.birth_date && moment(user.birth_date, 'YYYY-MM-DD').toDate(),
      jobTitle: user.job_title,
      roleId: user.id_role,
      type: user.type,
      description1: user.description1,
      description2: user.description2,
      description3: user.description3,
      fieldOfActivity: user.field_of_activity,
      trainings: user.trainings,
      careerSteps: user.career_steps,
      publications: user.publications,
      engagements: user.engagements,
      contributions: user.contributions,
      bedMember: Boolean(user.bed_member),
      bedMemberNumber: user.bed_member_nr,
      newsletterSubscribed: Boolean(user.newsletter_subscribed),
    };
  }

  public parseBackendListModel(user: UserBackendListModel): UserModel {
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      cooperationName: user.cooperation_name,
      firstname: user.first_name,
      lastname: user.last_name,
      email: user.email,
      phoneNumber: user.phone_number,
      type: user.type,
      jobTitle: user.job_title,
      description1: user.description1,
      description2: user.description2,
      description3: user.description3,
      avatarPath: user.avatar_path,
    };
  }

  public parseBackendNote(note: NoteBackendModel): NoteModel {
    return {
      id: note.id,
      text: note.text,
      createdAt: moment(note.created_at).toDate(),
      updatedAt: moment(note.updated_at).toDate(),
      user: this.parseBackendUser(note.user),
    };
  }
}
